import Menu from '@mui/material/Menu';
import classNames from 'classnames';
import React, { useState } from 'react';
import {
  Box,
  Card,
  CardActions,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Theme,
  Tooltip,
  Typography,
} from '@mui/material';
import { Feature } from '@loyalty/shared-common';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from '@qualifio/ui-commons/dist/components/qlf-prompt';
import { ProgramAttributes, ProgramStatus } from '@loyalty/shared-common/gamification-api-client';
import { colorPalette, useQlfTranslation } from '@qualifio/ui-commons';
import { faCode } from '@fortawesome/pro-solid-svg-icons/faCode';
import { faEdit } from '@fortawesome/pro-solid-svg-icons/faEdit';
import { faEllipsisV } from '@fortawesome/pro-solid-svg-icons/faEllipsisV';
import { faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons/faExclamationTriangle';
import { faSignalAlt3 } from '@fortawesome/pro-solid-svg-icons/faSignalAlt3';
import { faTrash } from '@fortawesome/pro-solid-svg-icons/faTrash';
import { faX } from '@fortawesome/pro-solid-svg-icons/faX';
import { makeStyles } from '@mui/styles';
import { useTranslation } from 'react-i18next';
import { LoyaltyItemActionButton } from './LoyaltyItemActionButton';
import { LoyaltyItemPreview } from './LoyaltyItemPreview';
import { LoyaltyItemTitle } from './LoyaltyItemTitle';
import { Router } from '../../../services';
import { TypeToConfirmModal } from '../../../shared/topbar/TypeToConfirmModal';
import { useAccounts } from '../../../accounts/AccountsContext';
import { useApi } from '../../../shared/api-client/ApiClientProvider';
import { useFeatures } from '../../../features/FeaturesContext';

const useStyles = makeStyles<Theme>(({ spacing, transitions }) => {
  const transition = transitions.create(['opacity', 'color'], {
    duration: transitions.duration.shorter,
  });

  return {
    root: {
      borderWidth: 2,
      height: 330,
      margin: spacing(1),
      position: 'relative',
      width: 275,
      '&:hover $media': {
        opacity: 0.4,
      },
      '&:hover $editIcon': {
        opacity: 1,
      },
      '&:hover $action': {
        opacity: `1 !important`,
      },
      '&:hover $deleteButton': {
        opacity: 1,
      },
    },
    editIcon: {
      opacity: 0,
    },
    deleteButton: {
      opacity: 0,
      zIndex: 2,
    },
    media: {
      opacity: 1,
    },
    actions: {
      display: 'flex',
      alignItems: 'center',
    },
    actionsBar: {
      justifyContent: 'flex-end',
    },
    action: {
      color: colorPalette.grey.main,
      fontSize: 16,
      transition,
      '&:hover': {
        backgroundColor: 'transparent',
        color: colorPalette.grey.dark,
      },
    },
    warning: {
      marginRight: '10px',
    },
    link: {
      textDecoration: 'none !important',
    },
    disabled: {
      pointerEvents: 'none',
      textDecoration: 'none !important',
    },
  };
});

interface Props {
  onEdit?: () => void;
  onPublish?: () => void;
  onEmbed?: () => void;
  loyaltyProgram: ProgramAttributes;
}

export function LoyaltyItem({
  onEdit = () => null,
  onEmbed = () => null,
  loyaltyProgram,
}: Props): React.ReactElement {
  const { root, link, disabled, editIcon, media, actions, action, actionsBar, warning } =
    useStyles();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const { t } = useTranslation();
  const { t: htmlT } = useQlfTranslation('translation');
  const { currentAccount } = useAccounts();
  const { managerApi } = useApi();
  const { features } = useFeatures();
  const { read: programRead, write: programWrite, remove: programDel } = features['PROGRAM'];
  const { enabled: embedEnabled } = features[Feature.EMBED];
  const { enabled: resetProgramEnabled } = features[Feature.RESET_PROGRAM];
  const deletingProgram = loyaltyProgram.status === ProgramStatus.DELETING;
  const deletingErrorProgram = loyaltyProgram.status === ProgramStatus.DELETING_ERROR;
  const deleting = deletingProgram || deletingErrorProgram;

  const [resetModalOpen, setResetModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const handleMenuClick = (event: any) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = (callback?: () => void) => () => {
    setAnchorEl(null);
    callback?.();
  };

  return (
    <Card className={root} id={`program-${loyaltyProgram.id}`}>
      <TypeToConfirmModal
        mutationKey={['program', loyaltyProgram.id, 'reset']}
        validationValue={loyaltyProgram.name}
        validationPlaceholder={t('program.modal.validation.placeholder')}
        isOpen={resetModalOpen}
        setIsOpen={setResetModalOpen}
        title={t('program.reset.dialog.title')}
        description={htmlT('program.reset.dialog.description', { name: loyaltyProgram.name })}
        buttonText={t('program.reset.dialog.confirm')}
        callBack={() =>
          managerApi
            .resetProgram(loyaltyProgram.id, loyaltyProgram.name)
            .then(() => window.location.reload())
        }
      />

      <TypeToConfirmModal
        mutationKey={['program', loyaltyProgram.id, 'delete']}
        validationValue={loyaltyProgram.name}
        validationPlaceholder={t('program.modal.validation.placeholder')}
        isOpen={deleteModalOpen}
        setIsOpen={setDeleteModalOpen}
        title={t('program.delete.dialog.title')}
        description={htmlT('loyalty.confirm.deletion.message', { name: loyaltyProgram.name })}
        buttonText={t('loyalty.confirm.deletion.button')}
        callBack={() =>
          managerApi.deleteProgram(loyaltyProgram.id).then(() => window.location.reload())
        }
      />

      <Tooltip title={deleting ? t('loyalty.tooltips.deleting') : ''} placement="top">
        <Link
          className={programRead ? link : disabled}
          to={Router.getPath('loyaltyProgram', {
            id: loyaltyProgram.id,
            accountId: currentAccount?.id,
          })}
        >
          <LoyaltyItemPreview
            title={loyaltyProgram.name}
            className={{ editIcon, media }}
            disabled={deleting || !programRead}
          />
          <LoyaltyItemTitle title={loyaltyProgram.name} />
        </Link>
      </Tooltip>
      <CardActions className={actionsBar}>
        <div className={actions}>
          {!loyaltyProgram.ssoProxyClientId && (
            <Tooltip title={t('loyalty.tooltips.no-sso-client')}>
              <span className={classNames(action, warning)}>
                <FontAwesomeIcon icon={faExclamationTriangle} />
              </span>
            </Tooltip>
          )}

          <LoyaltyItemActionButton
            enabled={embedEnabled}
            onClick={onEmbed}
            className={action}
            icon={faCode}
            tooltipMessage={t('loyalty.embed.title')}
            id={`embed-button-${loyaltyProgram.id}`}
          />
          <LoyaltyItemActionButton
            enabled={programWrite}
            onClick={onEdit}
            className={action}
            icon={faEdit}
            tooltipMessage={t('loyalty.tooltips.edit')}
            id={`edit-button-${loyaltyProgram.id}`}
          />
          <LoyaltyItemActionButton
            enabled={embedEnabled}
            onClick={handleMenuClick}
            className={action}
            icon={faEllipsisV}
            tooltipMessage={t('loyalty.tooltips.options')}
            id={`options-button-${loyaltyProgram.id}`}
          />
          <Menu anchorEl={anchorEl} open={open} onClose={handleMenuClose()}>
            <MenuItem
              disabled={!resetProgramEnabled}
              onClick={handleMenuClose(() => setResetModalOpen(true))}
              data-cy="purge-choice-id"
              id={`purge-choice-${loyaltyProgram.id}`}
            >
              <ListItemIcon>
                <Box sx={{ position: 'relative' }}>
                  <FontAwesomeIcon icon={faSignalAlt3} />
                  <FontAwesomeIcon
                    icon={faX}
                    size="xs"
                    color="red"
                    style={{ position: 'absolute', bottom: 0, right: 0, zIndex: 2 }}
                  />
                </Box>
              </ListItemIcon>
              <ListItemText>
                <Tooltip title={resetProgramEnabled ? '' : t('shared.action.permission.required')}>
                  <Typography>{t('topbar.tabs.settings.reset')}</Typography>
                </Tooltip>
              </ListItemText>
            </MenuItem>

            <MenuItem
              disabled={!programDel}
              onClick={handleMenuClose(() => setDeleteModalOpen(true))}
              data-cy="delete-choice-id"
              id={`delete-choice-${loyaltyProgram.id}`}
            >
              <ListItemIcon>
                <FontAwesomeIcon icon={faTrash} size="lg" />
              </ListItemIcon>
              <ListItemText>
                <Tooltip title={programDel ? '' : t('shared.action.permission.required')}>
                  <Typography>{t('loyalty.tooltips.delete')}</Typography>
                </Tooltip>
              </ListItemText>
            </MenuItem>
          </Menu>
        </div>
      </CardActions>
    </Card>
  );
}
